import { DEFAULT_TIMEZONE } from "constants/defaultTimezone";
import { SUPPORT_GOING_BACK_FROM_SESSION_REVIEW } from "constants/featureFlags";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { useUser } from "contexts/UserContext";
import useLocationParams from "hooks/useLocationParams";
import moment from "moment-timezone";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import getTimeRange from "../components/layout/components/calendar-view/utils/getTimeRange";

export type TIME_RANGE_TYPE = "day" | "threeDays" | "week" | "month";
export type TimeRange = number[];

type Context = {
  /**
   * Get new time range w/ incremental amount
   * @param amount number e.g. 1 week, 2 months
   */
  onChangeTimeRange: (amount: number) => void;
  currentTimeRangeType: TIME_RANGE_TYPE;
  setCurrentTimeRangeType: (arg: TIME_RANGE_TYPE) => void;
  /** Inherit from user's timezone and can be modified in calendar */
  currentCalendarTimezone: TimeZone;
  setCurrentCalendarTimezone: (arg: TimeZone) => void;
  anchorTime: number;
  setAnchorTime: (arg: number) => void;
} & { timeRange: ReturnType<typeof getTimeRange> };

const CalendarTimeContext = createContext<Context>({
  currentCalendarTimezone: DEFAULT_TIMEZONE,
  setCurrentCalendarTimezone: () => null,
  currentTimeRangeType: "week",
  setCurrentTimeRangeType: () => null,
  timeRange: [],
  onChangeTimeRange: () => null,
  anchorTime: +new Date(),
  setAnchorTime: () => null,
});

type Props = { children: ReactNode };

export const CalendarTimeContextProvider = ({ children }: Props) => {
  const { getParam, setParam } = useLocationParams();

  const { defaultUserTimezone } = useUser();
  const { isPhonePortrait } = usePhonePortraitContext();
  const defaultTimeRangeType = useMemo(
    () => (isPhonePortrait ? "threeDays" : "week"),
    [isPhonePortrait],
  );

  const [anchorTime, setAnchorTime] = useState(() => {
    const param = parseInt(getParam("anchor_time") ?? "");
    return isNaN(param) ? Date.now() : param;
  });
  useEffect(() => {
    if (!SUPPORT_GOING_BACK_FROM_SESSION_REVIEW) {
      return;
    }
    setParam("anchor_time", anchorTime.toString());
  }, [anchorTime, setParam]);

  const [currentTimeRangeType, setCurrentTimeRangeType] =
    useState<TIME_RANGE_TYPE>(() => {
      const param = getParam("time_range");
      if (param && ["day", "threeDays", "week", "month"].includes(param)) {
        return param as TIME_RANGE_TYPE;
      }
      return defaultTimeRangeType;
    });
  useEffect(() => {
    if (!SUPPORT_GOING_BACK_FROM_SESSION_REVIEW) {
      return;
    }
    setParam("time_range", currentTimeRangeType.toString());
  }, [currentTimeRangeType, setParam]);

  const [currentCalendarTimezone, setCurrentCalendarTimezone] =
    useState(defaultUserTimezone);

  useEffect(() => {
    setCurrentCalendarTimezone(defaultUserTimezone);
  }, [defaultUserTimezone]);

  const timeRange = useMemo(
    () =>
      getTimeRange(currentCalendarTimezone, currentTimeRangeType, anchorTime),
    [currentCalendarTimezone, currentTimeRangeType, anchorTime],
  );

  const onChangeTimeRange = useCallback(
    (amount: number) =>
      setAnchorTime((time) =>
        currentTimeRangeType === "threeDays"
          ? moment(time)
              .tz(currentCalendarTimezone)
              .add(amount * 3, "days")
              .valueOf()
          : moment(time)
              .tz(currentCalendarTimezone)
              .add(amount, currentTimeRangeType)
              .valueOf(),
      ),
    [currentCalendarTimezone, currentTimeRangeType],
  );

  return (
    <CalendarTimeContext.Provider
      value={{
        currentCalendarTimezone,
        setCurrentCalendarTimezone,
        currentTimeRangeType,
        setCurrentTimeRangeType,
        timeRange,
        onChangeTimeRange,
        anchorTime,
        setAnchorTime,
      }}
    >
      {children}
    </CalendarTimeContext.Provider>
  );
};

export const useCalendarTime = () => useContext(CalendarTimeContext);
